import { domain } from "@relode/core";
import { JobSalaryType } from "@relode/core/domain";
import * as Yup from "yup";

export const addJobSchema = Yup.object().shape({
    stepOne: Yup.object().shape({
        teamId: Yup.number()
            .default(undefined)
            .required("please select a team")
            .typeError("please select a team"),
        externalDescription: Yup.string()
            .when("attachment", {
                is: val => val === undefined || val === null || val === "",
                then: Yup.string()
                    .required("please enter a description")
                    .test({
                        name: "externalDescriptionRequired",
                        test: function (value: string) {
                            if (
                                value === undefined ||
                                value === null ||
                                value.trim() === ""
                            ) {
                                return this.createError({
                                    path: "stepOne.externalDescription",
                                    message:
                                        "please enter a description or upload an attachment",
                                });
                            }
                            return true;
                        },
                    }),
            })
            .nullable(true),
    }),
    stepTwo: Yup.object().shape({
        jobName: Yup.string()
            .default(undefined)
            .matches(
                /^[0-9a-zA-Z-()\s]+$/,
                "job title must be letters, numbers, spaces, parentheses, or dashes only"
            )
            .required("job title required"),
        normalizedJobName: Yup.string()
            .default(undefined)
            .matches(
                /^[0-9a-zA-Z-()\s]+$/,
                "job title must be letters, numbers, spaces, parentheses, or dashes only"
            )
            .required("normalized job title required"),
        location: Yup.object().required("location is required").nullable(true),
        normalizedLocation: Yup.object()
            .required("normalized location is required")
            .nullable(true),
        quantity: Yup.number()
            .default(undefined)
            .typeError("# of positions must be a number")
            .min(1, "# of positions must be 1 or more")
            .max(100, "# of positions cannot exceed 100")
            .required("# of positions is required"),
        jobTypeId: Yup.number()
            .default(undefined)
            .required("job type is required"),
        hoursPerWeek: Yup.number()
            .default(undefined)
            .required("please enter hours per week")
            .test({
                name: "hoursPerWeekTest",
                message: "invalid hours per week",
                test: function (value: number) {
                    if (value < 0 || value > 168) {
                        return this.createError({
                            path: "stepTwo.hoursPerWeek",
                            message: "hours per week must be between 0 and 168",
                        });
                    }
                    return true;
                },
            })
            .typeError("please enter hours per week"),
        jobSalaryInUSDollars: Yup.number()
            .required("please enter a salary")
            .test({
                name: "minDesiredSalaryMax",
                test: function (value: number) {
                    const { jobSalaryTypeId } = this.parent;
                    const result = checkSalary(jobSalaryTypeId, value);
                    return result === true
                        ? result
                        : this.createError({
                              path: "stepTwo.jobSalaryInUSDollars",
                              message: result,
                          });
                },
            })
            .default(undefined)
            .required("please enter a desired salary")
            .nullable(true),
        jobSalaryTypeId: Yup.number()
            .test({
                name: "salaryTypeRequired",
                test: function (value: number) {
                    const { jobTypeId } = this.parent;
                    if (
                        (value === null || value === undefined) &&
                        (jobTypeId === domain.JobType.FullTime.jobTypeId ||
                            jobTypeId === domain.JobType.PartTime.jobTypeId)
                    ) {
                        return this.createError({
                            path: "stepTwo.jobSalaryTypeId",
                            message: "please select a salary type",
                        });
                    }
                    return true;
                },
            })
            .nullable(true),
        contractStartDate: Yup.date()
            .when(["jobTypeId"], {
                is: id => domain.JobType.isContractOrLocum(id),
                then: Yup.date()
                    .required("date is required")
                    .typeError("invalid date"),
            })
            .nullable(true),
        contractDuration: Yup.number()
            .when(["jobTypeId"], {
                is: id => domain.JobType.isContractOrLocum(id),
                then: Yup.number()
                    .required("please enter a duration")
                    .typeError("please enter a duration")
                    .min(1, "please enter a duration greater than zero"),
            })
            .nullable(true),
        contractDurationTypeId: Yup.string()
            .when(["jobTypeId"], {
                is: id => domain.JobType.isContractOrLocum(id),
                then: Yup.string().required("please enter a length"),
            })
            .nullable(true),
    }),
});

const checkSalary = (salaryTypeId: number, salary: number) => {
    if (salary === null || salary === undefined) {
        return "please enter a desired salary";
    }
    if (JobSalaryType.isPerYear(salaryTypeId)) {
        return checkPerYearSalary(salary);
    }
    if (JobSalaryType.isPerHour(salaryTypeId)) {
        return checkPerHourSalary(salary);
    }

    return true;
};

const checkPerYearSalary = (salary: number) => {
    if (salary < 10000) {
        return "please enter a salary more than $10,000.00";
    } else if (salary > 1000000) {
        return "please enter a salary less than $1,000,000.00";
    }
    return true;
};

const checkPerHourSalary = (salary: number) => {
    if (salary < 10) {
        return "please enter a salary more than $10.00";
    } else if (salary > 1000) {
        return "please enter a salary less than $1,000.00";
    }

    return true;
};
